import { WarningOutlined } from '@ant-design/icons'
import type { ViewConfigSheet, ViewConfigSheetPreset } from '@salescore/core'
import { SHARED_LINK_PRESET_NAME_INPUT_PATTERN } from '@salescore/core'
import { Button, Form, Input, Table, Tag } from 'antd'
import { diff as deepObjectDiff } from 'deep-object-diff'
import { Array, pipe } from 'effect'
import { t } from 'i18next'
import { useState } from 'react'
import { useRecoilState } from 'recoil'

import {
  getDefinedPresetParameterKeys,
  parsePresetParameterKeys,
  type PresetParameterKeys,
} from '../../../../domain/viewConfig/sheetPresetParameterKeys'
import { useIsSavingSettings } from '../../../../hooks/useIsSavingSettings'
import { sheetPickedPresetNameAtom } from '../../../../recoil/navigation/atoms'
import type { SalescoreObjectAbility, ViewAbility } from '../../../../recoil/view/atoms'
import { configAtom } from '../../../../recoil/viewConfig'
import { recursiveRemoveEmptyValues } from '../../ViewUIKpiPivot/recursive'

interface SheetPresetFormValues {
  name: string
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
export function SheetPresetForm({
  preset: formPreset,
  onAfterFinish,
  ability,
}: {
  preset?: ViewConfigSheetPreset
  onAfterFinish: () => void
  ability: ViewAbility & SalescoreObjectAbility
}) {
  const [config, setConfig] = useRecoilState(configAtom)
  const [isSavingSettings, setIsSavingSettings] = useIsSavingSettings()
  const [picked, setPicked] = useRecoilState(sheetPickedPresetNameAtom)
  const [selectedRowKeys, setSelectedRowKeys] = useState(getDefinedPresetParameterKeys(formPreset ?? config))

  const dataSource: Array<{
    label: string
    key: PresetParameterKeys
    content: unknown
  }> = [
    {
      label: t(`表示項目`),
      key: `fields` as const,
      content: formPreset?.fields,
    },
    {
      label: t(`絞り込み`),
      key: `filterTree` as const,
      content: formPreset?.filterTree,
    },
    {
      label: t(`並び替え`),
      key: `sorters` as const,
      content: formPreset?.sorters,
    },
  ]

  function onFinish(values: SheetPresetFormValues) {
    if (!ability.canUpdate) {
      return
    }

    setConfig((previousConfig) => {
      if (previousConfig.type !== 'sheet') {
        return previousConfig
      }

      // 作成
      if (formPreset === undefined) {
        const newPreset = {
          name: values.name,
          tree: previousConfig.tree,
          fields: previousConfig.fields,
          filterTree: selectedRowKeys.includes('filterTree') ? previousConfig.filterTree : undefined,
          sorters: selectedRowKeys.includes('sorters') ? previousConfig.sorters : undefined,
          meta: previousConfig.meta,
        }
        const newPresets =
          previousConfig.presets === undefined
            ? [newPreset]
            : Array.dedupeWith([newPreset, ...previousConfig.presets], (a, b) => a.name === b.name) // 同名のプリセットがあれば上書き

        const { name, ...configPreset } = newPreset
        return {
          type: 'sheet',
          ...configPreset,
          presets: newPresets,
        }
      }

      // 更新
      const isNameNotUpdated = formPreset.name === values.name
      const presets =
        previousConfig.presets === undefined
          ? previousConfig.presets
          : pipe(
              previousConfig.presets,
              Array.filter((p) => isNameNotUpdated || p.name !== values.name),
              Array.map((p) =>
                p.name === formPreset.name
                  ? {
                      ...p,
                      name: values.name,
                      tree: selectedRowKeys.includes('fields') ? previousConfig.tree : p.tree,
                      fields: selectedRowKeys.includes('fields') ? previousConfig.fields : p.fields,
                      filterTree: selectedRowKeys.includes('filterTree') ? previousConfig.filterTree : p.filterTree,
                      sorters: selectedRowKeys.includes('sorters') ? previousConfig.sorters : p.sorters,
                    }
                  : p,
              ),
              Array.dedupeWith((a, b) => a.name === b.name),
            )
      return {
        ...previousConfig,
        presets,
      }
    })

    // 選択中のpresetを編集 or 新規作成時は対象のプリセットに明示的に切り替える
    if (formPreset?.name === picked || formPreset === undefined) {
      setPicked(values.name)
    }

    setIsSavingSettings(true) // useWatchView の useEffect で設定を保存するためにセットしている
    onAfterFinish()
  }

  if (config.type !== 'sheet' || !ability.canUpdate) {
    return null
  }

  return (
    <Form<SheetPresetFormValues>
      layout="vertical"
      initialValues={{
        name: formPreset?.name,
      }}
      onFinish={onFinish}
      validateMessages={{
        required: t(`プリセット名を入力してください`),
        pattern: {
          mismatch: t(`[共有リンク]から始まる文字列をプリセット名に使用することはできません`),
        },
      }}
    >
      <Form.Item
        name="name"
        label={t(`プリセット名`)}
        rules={[
          {
            required: true,
            pattern: SHARED_LINK_PRESET_NAME_INPUT_PATTERN,
          },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label={
          <>
            {t(`プリセットに含める設定`)}
            <Tag color="yellow" icon={<WarningOutlined />} style={{ marginLeft: 10 }}>
              {t(`現在のシートの設定内容でプリセットを上書きします`)}
            </Tag>
          </>
        }
      >
        <Table
          columns={[
            {
              dataIndex: `label`,
              width: 200,
            },
            {
              title: t(`設定内容`),
              width: 500,
              dataIndex: `content`,
              render: (_, record) => getParameterContent(config, record.key),
            },
            {
              title: ``,
              width: 500,
              dataIndex: `isOverriding`,

              render: (_, record) => {
                if (formPreset === undefined || record.key === 'name') {
                  return <></>
                }

                const oldValue = formPreset[record.key] ?? {}
                const newValue = config[record.key] ?? {}
                const diff = recursiveRemoveEmptyValues(deepObjectDiff(oldValue, newValue))
                if (Object.keys(diff).length === 0) {
                  return <></>
                }
                return (
                  <>
                    <Tag color="yellow" icon={<WarningOutlined />}>
                      {t(`下記の設定が上書きされます。`)}
                    </Tag>
                    <br />
                    <span className="text-yellow-600">
                      {t(`既存の設定内容`)}: {getParameterContent(formPreset, record.key)}
                    </span>
                  </>
                )
              },
            },
          ]}
          dataSource={dataSource}
          pagination={false}
          rowSelection={{
            type: 'checkbox',
            selectedRowKeys,
            onChange: (selectedRowKeys) => {
              setSelectedRowKeys(parsePresetParameterKeys(selectedRowKeys))
            },
            getCheckboxProps: (record) => ({
              // 新規作成時は開いているシートの列を踏襲するためfieldsを編集不可とする
              disabled: record.key === 'fields' && formPreset === undefined,
              name: record.key,
            }),
          }}
        />
      </Form.Item>
      <Form.Item className="flex justify-end">
        <Button type="primary" htmlType="submit" loading={isSavingSettings}>
          {t(`保存`)}
        </Button>
      </Form.Item>
    </Form>
  )
}

// eslint-disable-next-line sonarjs/function-return-type
function getParameterContent(config: ViewConfigSheet | ViewConfigSheetPreset | undefined, key: PresetParameterKeys) {
  switch (key) {
    // NOTE: useConnectionsSelectorを使ってmodel(+property)名まで出した方が親切か?
    case 'fields': {
      const fields = config?.fields
      return t(`列数 : ${fields?.length}`)
    }
    // eslint-disable-next-line sonarjs/todo-tag
    // TODO: どの項目(列)に設定がかかっているかの内訳も出したい
    case 'filterTree': {
      const filterTreeLeafs = config?.filterTree?.leafs
      return filterTreeLeafs !== undefined && filterTreeLeafs.length > 0
        ? t(`項目数 : ${filterTreeLeafs.length}`)
        : t(`未設定`)
    }
    case 'sorters': {
      const sorter = config?.sorters
      return sorter !== undefined && sorter.length > 0 ? t(`項目数 : ${sorter.length}`) : t(`未設定`)
    }
    default: {
      break
    }
  }

  return <></>
}
